<template>
    <el-aside class="side-menu" :width="$store.state.isCollapse ? '64px' : '200px'">
        <el-menu
            :collapse-transition="false"
            :collapse="$store.state.isCollapse"
            :router="true"
            :default-active="route.fullPath"
            :default-openeds="defaultOpeneds">
            <template v-for="menu in menus" :key="menu.path || menu.id">
                <el-sub-menu v-if="menu.children && menu.children.length" :index="menu.path">
                    <template #title>
                        <el-icon><component :is="iconMap[menu.icon] || Reading" /></el-icon>
                        <span>{{ menu.title }}</span>
                    </template>
                    <el-menu-item v-for="child in menu.children" :key="child.path || child.id" :index="child.path">
                        <el-badge
                            v-if="child.path === '/refundManage/manualRefundList'"
                            :value="$store.state.refundState.total"
                            :max="99"
                            :hidden="$store.state.refundState.hidden">
                            {{ child.title }}
                        </el-badge>
                        <span v-else>{{ child.title }}</span>
                    </el-menu-item>
                </el-sub-menu>
                <el-menu-item v-else :index="menu.path">
                    <el-icon><component :is="iconMap[menu.icon] || Reading" /></el-icon>
                    <span>{{ menu.title }}</span>
                </el-menu-item>
            </template>
        </el-menu>
    </el-aside>
</template>

<script setup>
import { computed, ref } from "vue";
import { UserFilled, Reading, List, WalletFilled, Connection, Goods, Setting, DataLine } from "@element-plus/icons-vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { getManualRefundListFun } from "@/api/refund";
import { getCurrentMenuTreeFun } from "@/api/menu";

const store = useStore();
const route = useRoute();
const iconMap = {
    UserFilled,
    Reading,
    List,
    WalletFilled,
    Connection,
    Goods,
    Setting,
    DataLine,
};

const fallbackMenus = [
    { title: "\u9996\u9875", path: "/dashboard", icon: "DataLine" },
    { title: "\u7528\u6237\u7ba1\u7406", path: "/userManage", icon: "UserFilled", children: [{ title: "\u7528\u6237\u5217\u8868", path: "/userManage/userList" }] },
    { title: "\u8ba2\u5355\u7ba1\u7406", path: "/consumptionManage", icon: "List", children: [{ title: "\u8bc4\u4f30\u8bb0\u5f55", path: "/consumptionManage/assessList" }, { title: "\u8ba2\u5355\u8bb0\u5f55", path: "/consumptionManage/consumptionList" }, { title: "\u7b2c\u4e09\u65b9\u8c03\u7528\u65e5\u5fd7", path: "/consumptionManage/thirdPartyInvokeLog" }] },
    { title: "\u9000\u6b3e\u7ba1\u7406", path: "/refundManage", icon: "WalletFilled", children: [{ title: "\u9000\u6b3e\u8bb0\u5f55", path: "/refundManage/refundList" }, { title: "\u624b\u52a8\u9000\u6b3e", path: "/refundManage/manualRefundList" }] },
    { title: "\u5145\u503c\u7ba1\u7406", path: "/rechargeManage", icon: "WalletFilled", children: [{ title: "\u7528\u6237\u5145\u503c", path: "/rechargeManage/rechargeRecord" }] },
    { title: "\u54c1\u724c\u7ba1\u7406", path: "/brandManage", icon: "Reading", children: [{ title: "\u5e38\u7528\u54c1\u724c\u7ba1\u7406", path: "/brandManage/brandList" }, { title: "\u7ef4\u4fdd\u54c1\u724c\u4ef7\u683c", path: "/brandManage/manufacturerPriceList" }] },
    { title: "\u4ea7\u54c1\u7ba1\u7406", path: "/productManage", icon: "Goods", children: [{ title: "\u4ea7\u54c1\u7ef4\u62a4", path: "/productManage/productList" }] },
    { title: "\u62a5\u8868\u7ba1\u7406", path: "/reportManage", icon: "Reading", children: [{ title: "\u62a5\u8868\u7ba1\u7406", path: "/reportManage/index" }, { title: "\u4e1a\u52a1\u62a5\u8868", path: "/reportManage/view/1247479377371467776" }] },
    { title: "\u5f00\u653e\u5e73\u53f0", path: "/openapiManage", icon: "Connection", children: [{ title: "\u5e94\u7528\u7ba1\u7406", path: "/openapiManage/appList" }, { title: "\u4ea7\u54c1\u6388\u6743", path: "/openapiManage/productAuthList" }, { title: "\u63a5\u53e3\u914d\u7f6e", path: "/openapiManage/interfaceConfig" }, { title: "\u5f00\u653e\u8ba2\u5355", path: "/openapiManage/openOrderList" }, { title: "\u8c03\u7528\u65e5\u5fd7", path: "/openapiManage/callRecordList" }, { title: "\u4f59\u989d\u7ba1\u7406", path: "/openapiManage/balanceList" }, { title: "\u5bf9\u8d26\u7edf\u8ba1", path: "/openapiManage/statementSummary" }] },
    { title: "\u7cfb\u7edf\u7ba1\u7406", path: "/systemManage", icon: "Setting", children: [{ title: "\u83dc\u5355\u7ba1\u7406", path: "/systemManage/menuList" }, { title: "\u5b57\u5178\u7ba1\u7406", path: "/systemManage/dictList" }] },
];

const menus = ref(fallbackMenus);
const defaultOpeneds = computed(() => menus.value.filter((menu) => menu.children && menu.children.length).map((menu) => menu.path));

const loadMenus = () => {
    getCurrentMenuTreeFun().then((res) => {
        if (res.code === 200 && Array.isArray(res.data) && res.data.length) {
            menus.value = res.data;
        }
    }).catch(() => {
        menus.value = fallbackMenus;
    });
};

const getManualRefundInfo = () => {
    const params = {
        keys: ["current", "size"],
        formValue: {
            current: 1,
            size: 100000,
        }
    };
    getManualRefundListFun(params).then((res) => {
        let total = 0;
        (res.data.records || []).forEach((row) => {
            if (["\u5904\u7406\u4e2d", "\u5f85\u5904\u7406"].includes(row.refundStatus)) {
                total += 1;
            }
        });
        store.commit("changeRefundState", total);
    });
};

loadMenus();
getManualRefundInfo();
</script>

<style lang="scss" scoped>
.side-menu {
    height: calc(100vh - 60px);
    background: #fff;
    border-right: 1px solid #e5e7eb;
    box-sizing: border-box;
    overflow-x: hidden;
    transition: width 0.2s ease;

    > .el-menu {
        height: 100%;
        border-right: none;
    }
}

:deep(.el-menu--collapse) {
    width: 64px;
}

:deep(.el-menu-item.is-active) {
    color: var(--admin-theme-color, #2d3a4b);
    background: var(--admin-theme-light, rgba(45, 58, 75, 0.1));
    font-weight: 600;
}

:deep(.el-menu-item.is-active::after) {
    content: "";
    position: absolute;
    right: 0;
    top: 12px;
    bottom: 12px;
    width: 3px;
    border-radius: 2px 0 0 2px;
    background: var(--admin-theme-color, #2d3a4b);
}

:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
    position: relative;
}

:deep(.el-menu-item:hover),
:deep(.el-sub-menu__title:hover) {
    color: var(--admin-theme-color, #2d3a4b);
    background: var(--admin-theme-light, rgba(45, 58, 75, 0.1));
}

:deep(.el-badge__content) {
    top: 15px !important;
    right: 0px !important;
}
</style>